import { useAuthorizationKeyListQuery } from '@graphql/queries/__generated__/authorization-keys.generated'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import FacebookLogin from '@greatsumini/react-facebook-login'
import { useGoogleLogin, GoogleOAuthProvider, CodeResponse } from '@react-oauth/google'
import Button from '@mui/material/Button'
import GoogleIcon from '@icons/GoogleIcon'
import FacebookIcon from '@icons/FacebookIcon'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles(theme => ({
  googleBtn: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    fontFamily: 'inherit !important',
    boxShadow: 'none !important',
    color: '#333 !important',
    borderColor: '#000 !important',
    fontSize: '12px !important',
    marginTop: 5,
    height: 44,
    '& > div': {
      background: 'inherit !important',
      padding: '5px 0 0 0 !important',
    },
    '&:hover': {
      background: '#eee !important',
      borderColor: 'rgba(0, 0, 0, 0.3) !important',
      outline: 'none !important',
    },
  },
  facebookBtn: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    color: '#333',
    backgroundColor: '#fff',
    borderColor: '#000',
    fontSize: '12px',
    marginTop: 5,
    padding: '7px 27px',
    height: 44,
    '& svg': {
      marginRight: 10,
      marginBottom: 4,
      fontSize: 22,
    },
  },
}))

type ProviderType = 'google-oauth2' | 'facebook'

const GoogleButton: React.FC<{
  responseSuccessGoogle(response: Omit<CodeResponse, 'error' | 'error_description' | 'error_uri'>): void
  responseErrorGoogle(response: Pick<CodeResponse, 'error' | 'error_description' | 'error_uri'>): void
}> = ({ responseSuccessGoogle, responseErrorGoogle }) => {
  const { t } = useTranslation()
  const login = useGoogleLogin({
    onSuccess: responseSuccessGoogle,
    onError: responseErrorGoogle,
    select_account: true,
  })
  return (
    <Button
      // type='submit'
      variant='outlined'
      size='large'
      onClick={() => login()}
      fullWidth
      startIcon={<GoogleIcon viewBox='0 0 48 48' fontSize='inherit' />}
    >
      {t('button.signInWithGoogle', 'Sign in with google')}
    </Button>
  )
}

interface SocialAuthButtonsProps {
  onSubmitSocialSignIn(values: { provider: ProviderType; accessToken: string }): Promise<void>

  register?: boolean
}

const SocialAuthButtons: React.FC<SocialAuthButtonsProps> = ({ onSubmitSocialSignIn, register }) => {
  const { t } = useTranslation()
  const classes = useStyles()

  const responseSuccessGoogle = async response => {
    await onSubmitSocialSignIn({
      provider: 'google-oauth2',
      accessToken: response.access_token,
    })
  }
  const responseErrorGoogle = response => {
    const msg = response.error === 'popup_closed_by_user' ? 'Popup closed by user' : response.error
    console.log(msg)
  }

  const { data } = useAuthorizationKeyListQuery()

  const responseFacebook = async response => {
    if (response?.status === 'unknown') {
    } else {
      await onSubmitSocialSignIn({
        provider: 'facebook',
        accessToken: response.accessToken,
      })
    }
  }

  const googleAuthorizationKey = data?.authorizationKeyList?.find(key => key.name === 'google-oauth2')
  const facebookAuthorizationKey = data?.authorizationKeyList?.find(key => key.name === 'facebook')
  return (
    <div className='mt-10'>
      {googleAuthorizationKey && (
        <GoogleOAuthProvider clientId={googleAuthorizationKey.key}>
          <GoogleButton responseErrorGoogle={responseErrorGoogle} responseSuccessGoogle={responseSuccessGoogle} />
        </GoogleOAuthProvider>
      )}
      {(googleAuthorizationKey || facebookAuthorizationKey) && (
        <div className='uppercase text-center my-5'>{t('or', 'or')}</div>
      )}
      {facebookAuthorizationKey && (
        <FacebookLogin
          appId={facebookAuthorizationKey.key}
          onSuccess={responseFacebook}
          className={classes.facebookBtn}
          onFail={error => {
            console.log('Login Failed!', error)
          }}
          onProfileSuccess={response => {
            console.log('Get Profile Success!', response)
          }}
        >
          <FacebookIcon viewBox='0 0 36 36' fontSize='inherit' />
          <span className='font-medium'>{t('button.signInWithFacebook', 'Sign in with Facebook')}</span>
        </FacebookLogin>
      )}
    </div>
  )
}

export default SocialAuthButtons
